import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Send, Copy, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Brand";
import { api, errMsg, useData } from "@/lib/api";
import { roleLabel } from "@/lib/crm";

const origin = () => window.location.origin;

function UserDialog({ open, user, onOpenChange, onSaved, onInvited }) {
  const [f, setF] = useState({ name: "", email: "", password: "", mode: "invite" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF({ name: user?.name || "", email: user?.email || "", password: "", mode: "invite" });
  }, [open, user]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (user) {
        await api.patch(`/admin/students/${user.id}`, { name: f.name, email: f.email });
        toast.success("User updated");
      } else {
        const invite = f.mode === "invite";
        const { data } = await api.post("/admin/students", { name: f.name, email: f.email, password: invite ? null : f.password, invite, origin: origin() });
        if (invite) toast.success(data.email_sent ? `Invitation emailed to ${f.email}` : `Invitation created for ${f.email}. Share the link shown.`);
        else toast.success(`${f.name} added. Their 30-day access starts at first login.`);
        if (invite) onInvited(data);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{user ? "Edit User" : "Add User"}</DialogTitle>
          <DialogDescription>{user ? "Update this AVY Associate's details." : "Add an AVY Associate. Their 30-day access begins on their first login."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Full name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="student-name-input" /></div>
          <div className="space-y-1.5"><Label>Email (login)</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="student-email-input" /></div>
          <div className="space-y-1.5"><Label>Role</Label><Input value="AVY Associate" disabled data-testid="student-role-input" /></div>
          {!user && (
            <div className="space-y-2">
              <Label>Access setup</Label>
              <RadioGroup value={f.mode} onValueChange={(mode) => setF({ ...f, mode })} className="space-y-2">
                <label className="flex items-start gap-2 text-sm"><RadioGroupItem value="invite" className="mt-0.5" data-testid="user-mode-invite" /><span><span className="font-semibold">Send invitation email</span><br /><span className="text-xs text-stone-500">They receive a link to create their account and choose a password.</span></span></label>
                <label className="flex items-start gap-2 text-sm"><RadioGroupItem value="password" className="mt-0.5" data-testid="user-mode-password" /><span><span className="font-semibold">Set a temporary password</span><br /><span className="text-xs text-stone-500">You share the credentials with them yourself.</span></span></label>
              </RadioGroup>
              {f.mode === "password" && <Input type="text" required minLength={6} placeholder="Temporary password (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="student-password-input" />}
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={busy} className="bg-[#3B185F]" data-testid="student-save-btn">{busy ? "Saving…" : user ? "Save changes" : f.mode === "invite" ? "Send invitation" : "Create user"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InviteLinkDialog({ data, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(data.invite_link); toast.success("Link copied"); };
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="invite-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{data?.email_sent === false ? "Invitation ready" : "Invitation sent"}</DialogTitle>
          <DialogDescription>{data?.email_sent === false
            ? `We couldn't deliver the email to ${data?.email}, but the invitation link below is active — share it with them directly.`
            : `An email with the link below was sent to ${data?.email}. You can also share it directly.`}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input readOnly value={data?.invite_link || ""} className="font-mono text-xs" data-testid="invite-link-input" />
          <Button variant="outline" onClick={copy} data-testid="invite-link-copy-btn"><Copy className="h-4 w-4" /></Button>
        </div>
        <p className="text-xs text-stone-500">The link expires in 7 days. Their 30-day access starts when they sign in for the first time.</p>
      </DialogContent>
    </Dialog>
  );
}

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

const FILTERS = {
  active: { label: "Active", match: (s) => s.status === "Active" },
  expiring: { label: "Expiring soon (≤7 days)", match: (s) => s.status === "Active" && s.days_left != null && s.days_left <= 7 },
  expired: { label: "Expired", match: (s) => s.status === "Expired" },
};

function Expiry({ s }) {
  if (s.status === "Not Yet Active") return <><div className="font-medium text-stone-800">Starts at first login</div>{s.invite_pending ? "Invitation pending" : "Added, not yet signed in"}</>;
  return <><div className="font-medium text-stone-800">{fmtDate(s.expires_at)}</div>{s.status === "Active" ? `${s.days_left} days left` : s.status}</>;
}

export default function AdminStudents({ onViewActivity, onViewTasks }) {
  const [students, reload] = useData("/admin/students", []);
  const [params, setParams] = useSearchParams();
  const [dlg, setDlg] = useState({ open: false, user: null });
  const [invited, setInvited] = useState(null);
  const filterKey = params.get("filter");
  const filter = FILTERS[filterKey];
  const shown = filter ? students.filter(filter.match) : students;
  const clearFilter = () => { const p = new URLSearchParams(params); p.delete("filter"); setParams(p); };

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const patch = (s, body, msg) => run(() => api.patch(`/admin/students/${s.id}`, body), msg);
  const resetPw = (s) => {
    const pw = window.prompt(`New password for ${s.name} (min 6 characters)`);
    if (pw && pw.length >= 6) patch(s, { password: pw }, "Password updated");
  };
  const resend = (s) => run(async () => { const { data } = await api.post(`/admin/students/${s.id}/invite`, { origin: origin() }); setInvited({ ...s, invite_link: data.invite_link, email_sent: data.email_sent }); }, `Invitation re-sent to ${s.email}`);
  const remove = (s) => {
    if (!window.confirm(`Remove ${s.name}? Their account and all of their contacts, appointments, campaigns, tasks and activity will be permanently deleted.`)) return;
    run(() => api.delete(`/admin/students/${s.id}`), `${s.name} removed`);
  };

  return (
    <div className="space-y-4" data-testid="user-management">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-stone-600">Add, invite, edit and manage AVY Associate accounts. Access runs for 30 days from each user's first login.</p>
        <Button onClick={() => setDlg({ open: true, user: null })} className="bg-[#3B185F]" data-testid="add-student-btn"><UserPlus className="h-4 w-4 mr-1.5" />Add User</Button>
      </div>
      {filter && (
        <div className="flex items-center gap-2 rounded-xl border border-[#D4AF37]/40 bg-[#FBF7EE] px-3 py-2 text-sm text-[#3B185F]" data-testid="active-filter-banner">
          <span className="font-semibold">Showing: {filter.label}</span>
          <span className="text-stone-500">({shown.length})</span>
          <button type="button" onClick={clearFilter} className="ml-auto inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-stone-600 hover:text-[#19082B] border" data-testid="clear-filter-btn"><X className="h-3 w-3" />Clear filter</button>
        </div>
      )}
      <div className="overflow-x-auto rounded-2xl border bg-white">
        <table className="w-full text-sm" data-testid="students-table">
          <thead className="bg-[#F6F2FB] text-left text-xs uppercase tracking-wider text-[#3B185F]">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Access Expiration</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} className="border-t" data-testid={`student-row-${s.id}`}>
                <td className="p-3 font-semibold text-[#19082B]">{s.name}</td>
                <td className="p-3 text-stone-600">{s.email}</td>
                <td className="p-3" data-testid={`student-role-${s.id}`}>{roleLabel(s.role)}</td>
                <td className="p-3"><StatusBadge status={s.status} testId={`student-status-${s.id}`} /></td>
                <td className="p-3 text-xs text-stone-600" data-testid={`student-expiry-${s.id}`}><Expiry s={s} /></td>
                <td className="p-3">
                  <div className="flex justify-end flex-wrap gap-1.5">
                    {s.status === "Not Yet Active" && s.is_active && <Button size="sm" variant="outline" className="border-[#D4AF37]" onClick={() => resend(s)} data-testid={`student-invite-${s.id}`}><Send className="h-3.5 w-3.5 mr-1" />{s.invite_pending ? "Resend invite" : "Send invite"}</Button>}
                    <Button size="sm" variant="outline" onClick={() => setDlg({ open: true, user: s })} data-testid={`student-edit-${s.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => onViewTasks(s.id)} data-testid={`student-view-tasks-${s.id}`}>Tasks</Button>
                    <Button size="sm" variant="outline" onClick={() => onViewActivity(s.id)} data-testid={`student-view-activity-${s.id}`}>Activity</Button>
                    {s.activated_at && <Button size="sm" variant="outline" onClick={() => patch(s, { renew: true }, "Access renewed for 30 days")} data-testid={`student-renew-${s.id}`}>Renew 30 days</Button>}
                    {!s.invite_pending && <Button size="sm" variant="outline" onClick={() => resetPw(s)} data-testid={`student-reset-pw-${s.id}`}>Reset password</Button>}
                    {s.is_active
                      ? <Button size="sm" variant="outline" className="text-red-600" onClick={() => patch(s, { is_active: false }, "User deactivated")} data-testid={`student-deactivate-${s.id}`}>Deactivate</Button>
                      : <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => patch(s, { is_active: true }, "User activated")} data-testid={`student-activate-${s.id}`}>Activate</Button>}
                    <Button size="sm" variant="ghost" className="text-stone-400 hover:text-red-600" onClick={() => remove(s)} data-testid={`student-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={6} className="p-10 text-center text-stone-500" data-testid="users-empty">{filter ? `No AVY Associates match "${filter.label}".` : 'No users yet. Click "Add User" to invite your first AVY Associate.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <UserDialog open={dlg.open} user={dlg.user} onOpenChange={(v) => setDlg((d) => ({ ...d, open: v }))} onSaved={reload} onInvited={setInvited} />
      <InviteLinkDialog data={invited} onClose={() => setInvited(null)} />
    </div>
  );
}
